import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import { Brain, Heart, Users, Compass, ArrowRight, Trash2, CreditCard, DollarSign, Target, AlertTriangle, Shield, ChevronDown, ChevronUp, Sparkles, Loader2 } from 'lucide-react';
import { collection, addDoc, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

const ProfileResults = ({ answers, subscription }) => {
    const { currentUser } = useAuth();
    const [isResetting, setIsResetting] = useState(false);
    const [expandedSections, setExpandedSections] = useState({});
    const [aiAnalyses, setAiAnalyses] = useState({});
    const [aiLoading, setAiLoading] = useState({});
    const [aiErrors, setAiErrors] = useState({});
    const [firestoreSubscription, setFirestoreSubscription] = useState(null);
    const [subscriptionLoaded, setSubscriptionLoaded] = useState(false);
    const hasData = answers && Object.keys(answers).length > 0;

    // Subscription-ul real: preferă Firestore (e cel mai exact), fallback pe prop
    const actualSubscription = firestoreSubscription || subscription || 'none';
    const isPremium = actualSubscription === 'premium';
    const hasSubscription = actualSubscription === 'premium' || actualSubscription === 'standard';

    const toggleSection = (key) => {
        setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // Load existing AI analyses AND subscription from Firestore
    useEffect(() => {
        const loadFromFirestore = async () => {
            if (!currentUser) return;
            try {
                const docRef = doc(db, 'userResponses', currentUser.uid);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    if (data.aiAnalyses) {
                        setAiAnalyses(data.aiAnalyses);
                    }
                    if (data.subscription) {
                        setFirestoreSubscription(data.subscription);
                        console.log('✅ Subscription din Firestore:', data.subscription);
                    }
                }
            } catch (err) {
                console.error('Error loading from Firestore:', err);
            } finally {
                setSubscriptionLoaded(true);
            }
        };
        loadFromFirestore();
    }, [currentUser]);

    // Generate AI analysis for a dimension
    const generateAnalysis = useCallback(async (dimension, dimensionData) => {
        if (!currentUser || !isPremium) return;

        setAiLoading(prev => ({ ...prev, [dimension]: true }));
        setAiErrors(prev => ({ ...prev, [dimension]: null }));

        try {
            const functions = getFunctions(undefined, 'europe-west1');
            const generateFn = httpsCallable(functions, 'generateAIAnalysis');

            const result = await generateFn({
                dimension,
                dimensionData,
                userName: currentUser.displayName || 'Utilizator'
            });

            if (result.data?.success && result.data?.text) {
                setAiAnalyses(prev => ({
                    ...prev,
                    [dimension]: {
                        text: result.data.text,
                        generatedAt: new Date().toISOString()
                    }
                }));
            } else if (result.data?.pending) {
                setAiErrors(prev => ({
                    ...prev,
                    [dimension]: 'Analiza este în curs de generare. Revino în câteva minute.'
                }));
            }
        } catch (err) {
            console.error(`AI Analysis error (${dimension}):`, err);
            const msg = err.message?.includes('permission-denied')
                ? 'Analiza AI este disponibilă doar pentru membrii Premium.'
                : err.message?.includes('resource-exhausted')
                    ? err.message
                    : 'Eroare la generarea analizei. Încearcă din nou.';
            setAiErrors(prev => ({ ...prev, [dimension]: msg }));
        } finally {
            setAiLoading(prev => ({ ...prev, [dimension]: false }));
        }
    }, [currentUser, isPremium]);

    // AI Analysis Box Component — one-time generation only
    const AIAnalysisBox = ({ dimension, dimensionData, borderColor = 'border-blue-200', bgColor = 'bg-blue-50', accentColor = 'text-blue-600' }) => {
        const analysis = aiAnalyses[dimension];
        const loading = aiLoading[dimension];
        const error = aiErrors[dimension];
        const hasAnalysis = analysis?.text;

        // Ascunde complet dacă subscription-ul s-a încărcat și NU e premium
        if (subscriptionLoaded && !isPremium) return null;
        // Încă se încarcă subscription-ul — nu arăta nimic încă
        if (!subscriptionLoaded) return null;

        return (
            <div className={`mt-4 rounded-xl border ${borderColor} overflow-hidden`}>
                {hasAnalysis ? (
                    <div className={`${bgColor} p-5`}>
                        <div className="flex items-center gap-2 mb-3">
                            <Sparkles className={accentColor} size={16} />
                            <span className={`text-sm font-semibold ${accentColor}`}>Analiză AI</span>
                        </div>
                        <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-line">{analysis.text}</p>
                        {analysis.generatedAt && (
                            <div className="text-xs text-slate-400 mt-3">
                                Generată: {new Date(analysis.generatedAt).toLocaleDateString('ro-RO')}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Sparkles size={16} className="text-amber-500" />
                            <span>Generează analiza AI personalizată</span>
                        </div>
                        <button
                            onClick={() => generateAnalysis(dimension, dimensionData)}
                            disabled={loading}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition ${loading
                                ? 'bg-slate-200 text-slate-500 cursor-wait'
                                : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 shadow-sm'
                                }`}
                        >
                            {loading ? (
                                <><Loader2 size={14} className="animate-spin" /> Se generează...</>
                            ) : (
                                <><Sparkles size={14} /> Analiză AI</>
                            )}
                        </button>
                    </div>
                )}
                {error && (
                    <div className="px-4 py-2 bg-red-50 border-t border-red-200 text-xs text-red-600">
                        {error}
                    </div>
                )}
            </div>
        );
    };

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('reset') === 'success') {
            window.history.replaceState({}, '', '/profile?tab=results');
            alert('✅ Testele au fost resetate cu succes! Pagina se va reîncărca...');
            setTimeout(() => window.location.reload(), 500);
        }
    }, []);

    const handleResetTests = async () => {
        if (!window.confirm('⚠️ ATENȚIE!\n\nResetarea testelor costă 39 €.\n\nToate răspunsurile tale vor fi șterse permanent!\n\nContinui?')) return;
        setIsResetting(true);
        try {
            const checkoutSessionRef = collection(db, 'customers', currentUser.uid, 'checkout_sessions');
            const docRef = await addDoc(checkoutSessionRef, {
                price: 'price_1T5wr8Q8au9doSF2mDZxH6J6',
                mode: 'payment',
                success_url: window.location.origin + '/profile?tab=results&reset=success',
                cancel_url: window.location.origin + '/profile?tab=results',
                payment_intent_data: { metadata: { type: 'reset_tests' } },
                metadata: { type: 'reset_tests' }
            });
            const unsubscribe = onSnapshot(docRef, async (snap) => {
                const data = snap.data();
                if (data?.url) { window.location.href = data.url; unsubscribe(); }
                if (data?.error) { alert('Eroare: ' + data.error.message); setIsResetting(false); unsubscribe(); }
            });
            setTimeout(() => { unsubscribe(); if (isResetting) { alert('Timeout.'); setIsResetting(false); } }, 60000);
        } catch (error) { alert('Eroare la plată.'); setIsResetting(false); }
    };

    // ==================== FRIENDLY LABELS ====================
    const mcmiLabels = {
        Schizoid: { friendly: 'Solitar & Rezervat', desc: 'Preferă solitudinea, confortabil cu independența emoțională', icon: '🏔️' },
        Masochist: { friendly: 'Altruist & Dedicat', desc: 'Se dedică celorlalți, uneori în detrimentul propriilor nevoi', icon: '🤲' },
        Dependent: { friendly: 'Orientat spre Conexiune', desc: 'Caută siguranță prin apropierea de ceilalți', icon: '🔗' },
        Narcisist: { friendly: 'Încrezător & Ambițios', desc: 'Încredere în sine și dorința de a fi remarcat', icon: '⭐' },
        Compulsiv: { friendly: 'Organizat & Perfecționist', desc: 'Nevoie de ordine, control și planificare', icon: '📐' },
        Evitant: { friendly: 'Precaut & Sensibil', desc: 'Prudent în relații, sensibil la respingere', icon: '🛡️' },
        Sadic: { friendly: 'Dominant & Asertiv', desc: 'Nevoie de control și putere în relații', icon: '👑' },
        Histrionic: { friendly: 'Expresiv & Sociabil', desc: 'Carismatic, deschis, centrul atenției', icon: '🎭' },
        Antisocial: { friendly: 'Nonconformist & Aventurier', desc: 'Independent, sfidează normele, asumă riscuri', icon: '🔥' },
        Psihopat: { friendly: 'Strategic & Detașat', desc: 'Rațional, calculat, detașat emoțional', icon: '♟️' },
        Negativist: { friendly: 'Sceptic & Critic', desc: 'Observator, critic, rezistent la autoritate', icon: '🔍' },
        Schizotipal: { friendly: 'Neconvențional & Creativ', desc: 'Gândire originală, perspective unice', icon: '🌀' },
        Borderline: { friendly: 'Intens & Pasional', desc: 'Emoții intense, oscilații, teamă de abandon', icon: '🌊' },
        Paranoid: { friendly: 'Vigilent & Precaut', desc: 'Alert, suspicios, mereu în gardă', icon: '👁️' },
        Anxios: { friendly: 'Sensibil & Prevăzător', desc: 'Anticipează riscuri, neliniște frecventă', icon: '🌧️' },
        Depresiv: { friendly: 'Introspectiv & Melancolic', desc: 'Profund, reflexiv, tendință spre tristețe', icon: '🌘' }
    };

    const riskSet = ['Borderline', 'Compulsiv', 'Evitant', 'Anxios', 'Depresiv', 'Psihopat', 'Sadic', 'Antisocial', 'Negativist', 'Paranoid'];

    // ==================== NIVEL 1 ====================

    const calculateNivel1Aggregate = () => {
        // Aggregate scoring from D1-D3 (exclude D4 like PHP)
        const letterCounts = { a: 0, b: 0, c: 0, d: 0, e: 0, f: 0 };
        let totalAnswered = 0;

        // D1: 5 questions
        for (let i = 1; i <= 5; i++) {
            const ans = answers[`n1d1q${i}`];
            if (ans && letterCounts.hasOwnProperty(ans)) { letterCounts[ans]++; totalAnswered++; }
        }
        // D2: 3 questions
        for (let i = 1; i <= 3; i++) {
            const ans = answers[`n1d2q${i}`];
            if (ans && letterCounts.hasOwnProperty(ans)) { letterCounts[ans]++; totalAnswered++; }
        }
        // D3: 4 questions
        for (let i = 1; i <= 4; i++) {
            const ans = answers[`n1d3q${i}`];
            if (ans && letterCounts.hasOwnProperty(ans)) { letterCounts[ans]++; totalAnswered++; }
        }

        if (totalAnswered === 0) return null;

        const dominant = Object.entries(letterCounts)
            .sort(([, a], [, b]) => b - a)[0];

        const profiles = {
            a: { name: 'Parteneriat Egal', desc: 'Preferi relații bazate pe echitate, contribuție egală și respect reciproc. Valorezi independența și colaborarea.' },
            b: { name: 'Tradițional Moderat', desc: 'Preferi un echilibru între roluri tradiționale și moderne. Apreciezi stabilitatea dar și flexibilitatea.' },
            c: { name: 'Pragmatic Flexibil', desc: 'Ești adapabil(ă) și practic(ă). Contează mai mult caracterul și atitudinea decât statusul formal.' },
            d: { name: 'Tradițional Clasic', desc: 'Preferi roluri clare și o structură tradițională în cuplu. Valorezi siguranța și protecția.' },
            e: { name: 'Emoțional Centrat', desc: 'Prioritizezi conexiunea emoțională și sprijinul reciproc peste aspectele materiale.' },
            f: { name: 'Independent Financiar', desc: 'Preferi autonomie financiară completă. Fiecare partener își gestionează propriile resurse.' }
        };

        return {
            dominant: dominant[0],
            count: dominant[1],
            total: totalAnswered,
            profile: profiles[dominant[0]] || { name: 'Mixt', desc: 'Profil combinat fără o dominanță clară.' },
            distribution: letterCounts
        };
    };

    const calculateNivel1Dim4Profile = () => {
        const profile = {};
        // Q1: money view
        if (answers.n1d4q1) profile.money = answers.n1d4q1 === 'economii' ? 'Econom' : 'Experiențial';
        // Q2: lifestyle (multiple)
        if (answers.n1d4q2 && Array.isArray(answers.n1d4q2)) profile.lifestyle = answers.n1d4q2;
        // Q3: relationship foundation
        if (answers.n1d4q3) profile.foundation = answers.n1d4q3 === 'sex' ? 'Intimitate Fizică' : 'Prietenie';
        // Q4: sexuality
        if (answers.n1d4q4) profile.sexuality = answers.n1d4q4 === 'traditional' ? 'Tradițional' : 'Explorativ';
        // Q5: relationship needs (multiple)
        if (answers.n1d4q5 && Array.isArray(answers.n1d4q5)) profile.needs = answers.n1d4q5;

        return Object.keys(profile).length > 0 ? profile : null;
    };

    const calculateNivel1Dim1 = () => {
        const responses = {};
        for (let i = 1; i <= 5; i++) { const qId = `n1d1q${i}`; if (answers[qId]) responses[qId] = answers[qId]; }
        if (Object.keys(responses).length === 0) return null;
        return { responses, count: Object.keys(responses).length, interpretation: 'Perspective despre roluri financiare și educație în relație' };
    };

    const calculateNivel1Dim2 = () => {
        const responses = {};
        for (let i = 1; i <= 3; i++) { const qId = `n1d2q${i}`; if (answers[qId]) responses[qId] = answers[qId]; }
        if (Object.keys(responses).length === 0) return null;
        return { responses, count: Object.keys(responses).length, interpretation: 'Echilibrul dintre autonomie și nevoia de sprijin' };
    };

    const calculateNivel1Dim3 = () => {
        const responses = {};
        for (let i = 1; i <= 4; i++) { const qId = `n1d3q${i}`; if (answers[qId]) responses[qId] = answers[qId]; }
        if (Object.keys(responses).length === 0) return null;
        return { responses, count: Object.keys(responses).length, interpretation: 'Viziunea asupra rolurilor și leadership-ului în cuplu' };
    };

    const calculateNivel1Dim4 = () => {
        const responses = {};
        for (let i = 1; i <= 5; i++) { const qId = `n1d4q${i}`; if (answers[qId]) responses[qId] = answers[qId]; }
        if (Object.keys(responses).length === 0) return null;
        return { responses, count: Object.keys(responses).length, interpretation: 'Preferințe de stil de viață și valori personale' };
    };

    // ==================== NIVEL 2 ====================

    const calculateAttachmentStyle = () => {
        const scores = { anxious: 0, avoidant: 0, secure: 0, disorganized: 0 };
        const questions = [
            { id: "n2d1q1", options: [{ value: "a", score: { anxious: 1 } }, { value: "b", score: { avoidant: 1 } }, { value: "c", score: { secure: 1 } }, { value: "d", score: { disorganized: 1 } }] },
            { id: "n2d1q2", options: [{ value: "a", score: { anxious: 1 } }, { value: "b", score: { avoidant: 1 } }, { value: "c", score: { secure: 1 } }, { value: "d", score: { disorganized: 1 } }] },
            { id: "n2d1q3", options: [{ value: "a", score: { anxious: 1 } }, { value: "b", score: { avoidant: 1 } }, { value: "c", score: { secure: 1 } }, { value: "d", score: { disorganized: 1 } }] },
            { id: "n2d1q4", options: [{ value: "a", score: { anxious: 1 } }, { value: "b", score: { avoidant: 1 } }, { value: "c", score: { secure: 1 } }, { value: "d", score: { disorganized: 1 } }] },
            { id: "n2d1q5", options: [{ value: "a", score: { anxious: 1 } }, { value: "b", score: { avoidant: 1 } }, { value: "c", score: { secure: 1 } }, { value: "d", score: { disorganized: 1 } }] }
        ];

        let hasAnyAnswers = false;
        questions.forEach(q => {
            const answer = answers[q.id];
            if (answer) {
                hasAnyAnswers = true;
                const option = q.options.find(opt => opt.value === answer);
                if (option?.score) Object.keys(option.score).forEach(key => { scores[key] += option.score[key]; });
            }
        });

        if (!hasAnyAnswers) return null;

        const maxScore = Math.max(...Object.values(scores));
        const dominantStyle = Object.keys(scores).find(key => scores[key] === maxScore);
        const styleInfo = {
            anxious: {
                name: 'Anxios',
                desc: 'Ai nevoie intensă de apropiere și reasigurare. Ești sensibil(ă) la semnele de distanțare ale partenerului și poți deveni neliniștit(ă) când nu primești confirmarea iubirii.',
                strengths: 'Empatic(ă), dedicat(ă), profund conectat(ă) emoțional',
                challenges: 'Tendință de a interpreta distanța ca respingere, nevoie frecventă de validare',
                compat: 'Cel mai compatibil cu stilul Securizant, care oferă stabilitatea de care ai nevoie',
                healing: 'Lucrează la construirea încrederii în sine și la tolerarea incertitudinii în relație'
            },
            avoidant: {
                name: 'Evitant',
                desc: 'Valorezi independența și autonomia. Intimitatea prea apropiată te poate face să simți că-ți pierzi libertatea. Preferi să procesezi emoțiile singur(ă).',
                strengths: 'Autonom(ă), stabil(ă) emoțional, rezistent(ă) la presiune',
                challenges: 'Dificultate în a fi vulnerabil(ă), tendință de retragere sub stres',
                compat: 'Cel mai compatibil cu stilul Securizant, care respectă nevoia ta de spațiu',
                healing: 'Practică deschiderea treptată și recunoașterea nevoii de conexiune'
            },
            secure: {
                name: 'Securizant',
                desc: 'Ești confortabil(ă) atât cu intimitatea cât și cu independența. Poți comunica deschis nevoile și emoțiile, gestionezi conflictele constructiv.',
                strengths: 'Echilibrat(ă), comunicativ(ă), empatic(ă), rezistent(ă) emoțional',
                challenges: 'Poate fi frustrant(ă) când partenerul nu comunică la același nivel',
                compat: 'Compatibil cu toate stilurile — ești un factor stabilizator în relație',
                healing: 'Continuă să-ți menții echilibrul și să oferi siguranță partenerului'
            },
            disorganized: {
                name: 'Dezorganizat',
                desc: 'Oscilezi între dorința de apropiere și teama de ea. Relațiile pot fi intense și imprevizibile, cu momente de vulnerabilitate extremă urmate de retragere.',
                strengths: 'Profunzime emoțională, intuiție puternică, capacitate de transformare',
                challenges: 'Reacții contradictorii, dificultate în a menține consistența emoțională',
                compat: 'Beneficiezi cel mai mult de un partener Securizant cu răbdare și constanță',
                healing: 'Terapia de atașament poate fi foarte benefică — lucrează la identificarea tiparelor'
            }
        };
        return { style: styleInfo[dominantStyle]?.name || 'Necunoscut', info: styleInfo[dominantStyle], scores };
    };

    const calculateRelationalValues = () => {
        const values = { stabilitate: 0, libertate: 0, evolutie: 0, aventura: 0, loialitate: 0, spiritualitate: 0 };
        let hasAnyAnswers = false;
        ['n2d2q1', 'n2d2q2', 'n2d2q3', 'n2d2q4', 'n2d2q5'].forEach(qId => {
            const answer = answers[qId];
            if (answer && values.hasOwnProperty(answer)) { values[answer]++; hasAnyAnswers = true; }
        });
        return hasAnyAnswers ? values : null;
    };

    const calculateEmotionalMaturity = () => {
        let totalScore = 0, answeredCount = 0;
        const questions = [
            { id: "n2d3q1", opts: [{ v: "a", s: 1 }, { v: "b", s: 3 }, { v: "c", s: 0 }, { v: "d", s: 4 }] },
            { id: "n2d3q2", opts: [{ v: "a", s: 2 }, { v: "b", s: 1 }, { v: "c", s: 4 }, { v: "d", s: 2 }] },
            { id: "n2d3q3", opts: [{ v: "a", s: 1 }, { v: "b", s: 4 }, { v: "c", s: 2 }, { v: "d", s: 1 }] },
            { id: "n2d3q4", opts: [{ v: "a", s: 1 }, { v: "b", s: 0 }, { v: "c", s: 4 }, { v: "d", s: 2 }] },
            { id: "n2d3q5", opts: [{ v: "a", s: 1 }, { v: "b", s: 4 }, { v: "c", s: 2 }, { v: "d", s: 2 }] },
            { id: "n2d3q6", opts: [{ v: "a", s: 1 }, { v: "b", s: 2 }, { v: "c", s: 4 }, { v: "d", s: 2 }] }
        ];
        questions.forEach(q => {
            const ans = answers[q.id];
            const opt = q.opts.find(o => o.v === ans);
            if (opt) { totalScore += opt.s; answeredCount++; }
        });
        if (answeredCount === 0) return null;

        let profile, description, colorClass, detailedDesc;
        if (totalScore <= 9) {
            profile = 'Reactiv'; colorClass = 'red';
            description = 'Dificultăți în reglarea emoțională';
            detailedDesc = 'Reacționezi adesea impulsiv sub stres, cu tendința de a te retrage sau a ataca. Emoțiile te copleșesc și îți este greu să le gestionezi constructiv. Lucrul cu un terapeut poate accelera foarte mult progresul.';
        } else if (totalScore <= 15) {
            profile = 'În Dezvoltare'; colorClass = 'orange';
            description = 'Conștientizare în creștere';
            detailedDesc = 'Începi să recunoști tiparele emoționale, dar încă oscilezi între reacție și reflecție. Progresezi, dar ai nevoie de practică consistentă pentru a-ți consolida abilitățile de reglare.';
        } else if (totalScore <= 21) {
            profile = 'Stabil'; colorClass = 'yellow';
            description = 'Comunicare asertivă consolidată';
            detailedDesc = 'Ai o bază solidă de maturitate emoțională. Poți comunica asertiv în majoritatea situațiilor și îți gestionezi emoțiile cu conștiinciozitate. Uneori, sub presiune, poți reveni la tipare mai vechi.';
        } else {
            profile = 'Matur'; colorClass = 'green';
            description = 'Reglare emoțională sănătoasă';
            detailedDesc = 'Demonstrezi o capacitate remarcabilă de auto-reglare. Poți naviga conflictele cu empatie, îți asumi responsabilitatea emoțională și oferi un spațiu sigur partenerului. Ești un factor stabilizator în relație.';
        }
        return { totalScore, maxPossible: 24, profile, description, detailedDesc, colorClass };
    };

    const calculateLoveLanguages = () => {
        const map = { n2d4q1: 'timp_calitate', n2d4q2: 'cadouri', n2d4q3: 'cuvinte_afirmare', n2d4q4: 'contact_fizic', n2d4q5: 'acte_serviciu' };
        const languages = {};
        Object.entries(map).forEach(([qId, cat]) => {
            if (answers[qId] !== undefined && answers[qId] !== null) languages[cat] = answers[qId];
        });
        return Object.keys(languages).length > 0 ? languages : null;
    };

    const calculateEmotionalIntelligence = () => {
        let totalScore = 0, answeredCount = 0;
        for (let i = 1; i <= 10; i++) {
            const ans = answers[`n2d5q${i}`];
            if (ans) { totalScore += ({ a: 1, b: 2, c: 3, d: 4 }[ans] || 0); answeredCount++; }
        }
        if (answeredCount === 0) return null;

        let profile, description, colorClass, detailedDesc;
        if (totalScore <= 19) {
            profile = 'Evitant Reactiv'; colorClass = 'red';
            description = 'Evită apropierea emoțională';
            detailedDesc = 'Ai tendința de a te proteja prin distanțare emoțională. Vulnerabilitatea este percepută ca amenințare. Relațiile pot fi superficiale sau marcate de conflicte nerezolvate.';
        } else if (totalScore <= 25) {
            profile = 'Anxios Nesigur'; colorClass = 'orange';
            description = 'Dependență emoțională latentă';
            detailedDesc = 'Cauți validare externă pentru a te simți în siguranță. Emoțiile partenerului îți influențează puternic starea. Lucrezi la construirea unei baze interne de securitate.';
        } else if (totalScore <= 32) {
            profile = 'În Tranziție'; colorClass = 'yellow';
            description = 'Lucrează activ cu sine';
            detailedDesc = 'Ești într-un proces activ de creștere emoțională. Recunoști tiparele, înveți din experiențe și cauți să dezvolți relații mai sănătoase. Progresul tău este vizibil.';
        } else {
            profile = 'Partener Conștient'; colorClass = 'green';
            description = 'Autoreglare și empatie dezvoltată';
            detailedDesc = 'Ai o inteligență emoțională ridicată. Poți naviga complexitatea relațiilor cu echilibru, empatie și maturitate. Ești capabil(ă) de intimitate autentică.';
        }
        return { totalScore, maxPossible: 40, profile, description, detailedDesc, colorClass };
    };

    // ==================== NIVEL 3 ====================

    const calculateSubconsciousPatterns = () => {
        const patterns = { a: 0, b: 0, c: 0, d: 0 };
        let hasAnyAnswers = false;
        ['n3q1', 'n3q2', 'n3q3', 'n3q4', 'n3q5', 'n3q6', 'n3q7'].forEach(qId => {
            const ans = answers[qId];
            if (ans && patterns.hasOwnProperty(ans)) { patterns[ans]++; hasAnyAnswers = true; }
        });
        if (!hasAnyAnswers) return null;

        const max = Object.entries(patterns).reduce((m, [k, v]) => v > m.val ? { key: k, val: v } : m, { key: 'a', val: 0 });
        const info = {
            a: {
                name: 'Atragere spre indisponibilitate',
                desc: 'Protecție prin evitare',
                wound: 'Rana de abandon sau respingere din copilărie. Ai învățat că iubirea vine cu distanță, iar apropierea reală te face vulnerabil(ă).',
                dynamics: 'Tinde să aleagă parteneri emoțional sau fizic indisponibili. Confuzia dintre „provocare" și „iubire" menține ciclul.',
                healing: 'Recunoaște tiparul: „indisponibilitatea nu este mister, este lipsă". Lucrează la tolerarea relațiilor stabile.',
                compatNote: 'Ai nevoie de un partener prezent și constant care să nu te lase să fugi din relație.'
            },
            b: {
                name: 'Complexul salvatorului',
                desc: 'Valoare prin ajutor',
                wound: 'Rana de nerecunoaștere. Ai învățat că ești valoros(oasă) doar când ajuți, salvezi sau repari pe altcineva.',
                dynamics: 'Atragi și ești atras(ă) de persoane care „au nevoie" de tine. Relația devine asimetrică: dai mult, primești puțin.',
                healing: 'Învață că meriti iubire fără să „faci" ceva pentru ea. Pune-ți propriile nevoi pe primul loc.',
                compatNote: 'Ai nevoie de un partener autonom care te iubește pentru cine ești, nu pentru ce faci.'
            },
            c: {
                name: 'Atracție față de haos',
                desc: 'Confuzie între iubire și neliniște',
                wound: 'Rana de instabilitate din copilărie. Sistemul nervos a fost „antrenat" să asocieze drama cu intensitatea iubirii.',
                dynamics: 'Relațiile calme par plictisitoare. Cauți emoții intense, suișuri și coborâșuri, confundând adrenalina cu pasiunea.',
                healing: 'Recalibrează: liniștea nu înseamnă lipsă de iubire. Practicezi tolerarea stabilității ca formă de siguranță.',
                compatNote: 'Ai nevoie de un partener stabil care te ajută să înveți că siguranța emoțională este fundația iubirii.'
            },
            d: {
                name: 'Repetarea relației cu părinții',
                desc: 'Căutarea iubirii negăsite',
                wound: 'Rana primară de atașament. Încerci inconștient să „repari" relația cu părintele prin alegerile romantice.',
                dynamics: 'Alegi parteneri care reproduc dinamica parentală — fie pentru a obține iubirea neacordată, fie pentru familiaritate.',
                healing: 'Conștientizează legătura: „Nu caut un partener, caut aprobarea părintelui". Separă trecutul de prezent.',
                compatNote: 'Ai nevoie de un partener care nu reproduce tiparele familiale și te ajută să construiești o nouă definiție a iubirii.'
            }
        };
        return { dominant: info[max.key], scores: patterns, dominantKey: max.key };
    };

    // ==================== NIVEL 4 ====================

    const calculatePersonalityTraits = () => {
        const cats = ['Schizoid', 'Masochist', 'Dependent', 'Narcisist', 'Compulsiv', 'Evitant', 'Sadic', 'Histrionic', 'Antisocial', 'Psihopat', 'Negativist', 'Schizotipal', 'Borderline', 'Paranoid', 'Anxios', 'Depresiv'];
        const counts = { Schizoid: 3, Masochist: 4, Dependent: 3, Narcisist: 3, Compulsiv: 3, Evitant: 3, Sadic: 4, Histrionic: 3, Antisocial: 3, Psihopat: 3, Negativist: 4, Schizotipal: 2, Borderline: 4, Paranoid: 2, Anxios: 4, Depresiv: 4 };
        const scores = {};

        cats.forEach(cat => {
            let total = 0, count = 0;
            for (let i = 1; i <= counts[cat]; i++) {
                const score = answers[`n4_${cat.toLowerCase()}_${i}`];
                if (score) { total += score; count++; }
            }
            if (count > 0) scores[cat] = { average: total / count, count, total: counts[cat] };
        });

        if (Object.keys(scores).length === 0) return null;

        // Level per category
        const categorized = {};
        Object.entries(scores).forEach(([cat, data]) => {
            let level, levelColor;
            if (data.average <= 2.5) { level = 'Scăzut'; levelColor = 'green'; }
            else if (data.average <= 3.5) { level = 'Mediu'; levelColor = 'yellow'; }
            else { level = 'Ridicat'; levelColor = 'red'; }
            categorized[cat] = { ...data, level, levelColor };
        });

        // Vulnerabilities (≥ 3.6)
        const vulnerabilities = Object.entries(categorized)
            .filter(([, d]) => d.average >= 3.6)
            .sort(([, a], [, b]) => b.average - a.average);

        // Risk flags (> 3.5 in risk set)
        const riskFlags = Object.entries(categorized)
            .filter(([cat, d]) => d.average > 3.5 && riskSet.includes(cat))
            .sort(([, a], [, b]) => b.average - a.average);

        // Top 3
        const topTraits = Object.entries(categorized)
            .sort(([, a], [, b]) => b.average - a.average)
            .slice(0, 3);

        // All sorted
        const allSorted = Object.entries(categorized)
            .sort(([, a], [, b]) => b.average - a.average);

        return { categoryScores: categorized, topTraits, allSorted, vulnerabilities, riskFlags };
    };

    // ==================== NO DATA ====================

    if (!hasData) {
        // Dacă subscription-ul nu s-a încărcat încă, arată loading
        const targetUrl = !subscriptionLoaded ? '/teste' : (hasSubscription ? '/teste' : '/pricing');
        return (
            <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 sm:p-12 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-600">
                            <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
                        </svg>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">Încă nu ai completat niciun test</h3>
                    <p className="text-base sm:text-lg text-slate-600 mb-6">Completează testele psihologice pentru a vedea rezultatele tale detaliate aici.</p>
                    <button onClick={() => window.location.href = targetUrl} className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-semibold transition shadow-md inline-flex items-center gap-2">
                        Începe Testele <ArrowRight size={20} />
                    </button>
                </div>
            </div>
        );
    }

    // ==================== CALCULATE ALL ====================

    const nivel1Aggregate = calculateNivel1Aggregate();
    const nivel1Dim4Profile = calculateNivel1Dim4Profile();
    const nivel1dim1 = calculateNivel1Dim1();
    const nivel1dim2 = calculateNivel1Dim2();
    const nivel1dim3 = calculateNivel1Dim3();
    const nivel1dim4 = calculateNivel1Dim4();
    const attachment = calculateAttachmentStyle();
    const values = calculateRelationalValues();
    const maturity = calculateEmotionalMaturity();
    const loveLanguages = calculateLoveLanguages();
    const emotionalIQ = calculateEmotionalIntelligence();
    const subconscious = calculateSubconsciousPatterns();
    const personality = calculatePersonalityTraits();

    const valueLabels = { stabilitate: 'Stabilitate', libertate: 'Libertate', evolutie: 'Evoluție', aventura: 'Aventură', loialitate: 'Loialitate', spiritualitate: 'Spiritualitate' };
    const loveLanguageLabels = { timp_calitate: 'Timp de Calitate', cadouri: 'Cadouri', cuvinte_afirmare: 'Cuvinte de Afirmare', contact_fizic: 'Contact Fizic', acte_serviciu: 'Acte de Serviciu' };

    const topValues = values ? Object.entries(values).sort(([, a], [, b]) => b - a).slice(0, 3) : [];
    const topLanguages = loveLanguages ? Object.entries(loveLanguages).sort(([, a], [, b]) => b - a).slice(0, 3) : [];

    const maturityColors = {
        red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', bar: 'bg-red-500' },
        orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', bar: 'bg-orange-500' },
        yellow: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', bar: 'bg-yellow-500' },
        green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', bar: 'bg-green-500' }
    };
    const mColors = maturity ? maturityColors[maturity.colorClass] : null;
    const eqColors = emotionalIQ ? maturityColors[emotionalIQ.colorClass] : null;

    const lifestyleLabels = {
        calatorii: 'Călătorii', iesiri_prieteni: 'Ieșiri cu prietenii', plimbari_natura: 'Natură',
        experiente_noi: 'Experiențe noi', acasa: 'Acasă', activitati_solitare: 'Activități solitare',
        socializare_limitata: 'Socializare selectivă'
    };
    const needsLabels = {
        companie: 'Companie', sprijin_emotional: 'Sprijin emoțional', apartenenta: 'Apartenență',
        dezvoltare: 'Dezvoltare', sprijin_financiar: 'Sprijin financiar', iubire: 'Iubire',
        sex: 'Sex', sa_nu_fiu_singur: 'Să nu fiu singur(ă)'
    };

    // ==================== RENDER ====================

    return (
        <div className="max-w-5xl mx-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl shadow-lg p-8 mb-8 text-white">
                <div className="flex items-center justify-between">
                    <div><h2 className="text-3xl font-bold mb-2">Rezultatele Tale Psihologice</h2><p className="text-blue-100">Analiză completă pe 4 niveluri</p></div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6 text-center">
                        <div className="text-4xl font-bold">{Object.keys(answers).length}</div>
                        <div className="text-sm text-blue-100">Răspunsuri</div>
                    </div>
                </div>
            </div>

            {/* ==================== NIVEL 1 ==================== */}

            {/* Nivel 1 Aggregate */}
            {nivel1Aggregate && (
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-4 sm:p-8 mb-6 sm:mb-8">
                    <div className="flex items-center mb-6 pb-4 border-b">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                            <Users className="text-blue-600" size={24} />
                        </div>
                        <div>
                            <h2 className="text-xl sm:text-2xl font-bold">Profil Standarde Extrinseci</h2>
                            <p className="text-slate-600 text-sm">Nivel 1 — Sinteza dimensiunilor D1-D3</p>
                        </div>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-6 border border-blue-200 mb-4">
                        <div className="text-sm font-medium text-blue-600 mb-2">PROFIL DOMINANT</div>
                        <div className="text-2xl font-bold text-blue-900 mb-3">{nivel1Aggregate.profile.name}</div>
                        <p className="text-slate-700">{nivel1Aggregate.profile.desc}</p>
                        <div className="text-sm text-slate-500 mt-3">Bazat pe {nivel1Aggregate.total} răspunsuri din 12</div>
                    </div>
                    <AIAnalysisBox
                        dimension="nivel1_aggregate"
                        dimensionData={{ profileName: nivel1Aggregate.profile.name, profileDesc: nivel1Aggregate.profile.desc, distribution: nivel1Aggregate.distribution }}
                        borderColor="border-blue-200" bgColor="bg-blue-50" accentColor="text-blue-600"
                    />
                </div>
            )}

            {/* Nivel 1 Dimensions */}
            {nivel1dim1 && (
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-4 sm:p-8 mb-6 sm:mb-8">
                    <div className="flex items-center mb-6 pb-4 border-b">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4"><DollarSign className="text-blue-600" size={24} /></div>
                        <div><h2 className="text-xl sm:text-2xl font-bold">Statut Economic-Social</h2><p className="text-slate-600 text-sm">Nivel 1 — Dimensiunea 1</p></div>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                        <p className="text-slate-700 text-lg">{nivel1dim1.interpretation}</p>
                        <div className="text-sm text-slate-600 mt-4">{nivel1dim1.count}/5 completat</div>
                    </div>
                </div>
            )}

            {nivel1dim2 && (
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-4 sm:p-8 mb-6 sm:mb-8">
                    <div className="flex items-center mb-6 pb-4 border-b">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4"><Users className="text-green-600" size={24} /></div>
                        <div><h2 className="text-xl sm:text-2xl font-bold">Independență vs Sprijin</h2><p className="text-slate-600 text-sm">Nivel 1 — Dimensiunea 2</p></div>
                    </div>
                    <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                        <p className="text-slate-700 text-lg">{nivel1dim2.interpretation}</p>
                        <div className="text-sm text-slate-600 mt-4">{nivel1dim2.count}/3 completat</div>
                    </div>
                </div>
            )}

            {nivel1dim3 && (
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-4 sm:p-8 mb-6 sm:mb-8">
                    <div className="flex items-center mb-6 pb-4 border-b">
                        <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4"><Target className="text-orange-600" size={24} /></div>
                        <div><h2 className="text-xl sm:text-2xl font-bold">Roluri Gen & Leadership</h2><p className="text-slate-600 text-sm">Nivel 1 — Dimensiunea 3</p></div>
                    </div>
                    <div className="bg-orange-50 rounded-xl p-6 border border-orange-200">
                        <p className="text-slate-700 text-lg">{nivel1dim3.interpretation}</p>
                        <div className="text-sm text-slate-600 mt-4">{nivel1dim3.count}/4 completat</div>
                    </div>
                </div>
            )}

            {/* Nivel 1 D4 — Preference Profile */}
            {nivel1Dim4Profile && (
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-4 sm:p-8 mb-6 sm:mb-8">
                    <div className="flex items-center mb-6 pb-4 border-b">
                        <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-4"><Compass className="text-teal-600" size={24} /></div>
                        <div><h2 className="text-xl sm:text-2xl font-bold">Profil Stil de Viață</h2><p className="text-slate-600 text-sm">Nivel 1 — Dimensiunea 4 — Preferințe</p></div>
                    </div>
                    <div className="space-y-4">
                        {nivel1Dim4Profile.money && (
                            <div className="bg-teal-50 rounded-xl p-5 border border-teal-200">
                                <div className="text-sm font-medium text-teal-600 mb-1">PERSPECTIVĂ FINANCIARĂ</div>
                                <div className="text-lg font-semibold text-slate-900">{nivel1Dim4Profile.money}</div>
                            </div>
                        )}
                        {nivel1Dim4Profile.foundation && (
                            <div className="bg-teal-50 rounded-xl p-5 border border-teal-200">
                                <div className="text-sm font-medium text-teal-600 mb-1">FUNDAMENT RELAȚIE</div>
                                <div className="text-lg font-semibold text-slate-900">{nivel1Dim4Profile.foundation}</div>
                            </div>
                        )}
                        {nivel1Dim4Profile.sexuality && (
                            <div className="bg-teal-50 rounded-xl p-5 border border-teal-200">
                                <div className="text-sm font-medium text-teal-600 mb-1">ORIENTARE INTIMITATE</div>
                                <div className="text-lg font-semibold text-slate-900">{nivel1Dim4Profile.sexuality}</div>
                            </div>
                        )}
                        {nivel1Dim4Profile.lifestyle && nivel1Dim4Profile.lifestyle.length > 0 && (
                            <div className="bg-teal-50 rounded-xl p-5 border border-teal-200">
                                <div className="text-sm font-medium text-teal-600 mb-2">PREFERINȚE TIMP LIBER</div>
                                <div className="flex flex-wrap gap-2">
                                    {nivel1Dim4Profile.lifestyle.map(v => (
                                        <span key={v} className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-sm font-medium">{lifestyleLabels[v] || v}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                        {nivel1Dim4Profile.needs && nivel1Dim4Profile.needs.length > 0 && (
                            <div className="bg-teal-50 rounded-xl p-5 border border-teal-200">
                                <div className="text-sm font-medium text-teal-600 mb-2">NEVOI ÎN RELAȚIE</div>
                                <div className="flex flex-wrap gap-2">
                                    {nivel1Dim4Profile.needs.map(v => (
                                        <span key={v} className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-sm font-medium">{needsLabels[v] || v}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <AIAnalysisBox
                        dimension="nivel1_dim4"
                        dimensionData={nivel1Dim4Profile}
                        borderColor="border-teal-200" bgColor="bg-teal-50" accentColor="text-teal-600"
                    />
                </div>
            )}

            {/* ==================== NIVEL 2 ==================== */}

            {/* Attachment — Enhanced */}
            {attachment && (
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-4 sm:p-8 mb-6 sm:mb-8">
                    <div className="flex items-center mb-6 pb-4 border-b">
                        <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mr-4"><Heart className="text-purple-600" size={24} /></div>
                        <div><h2 className="text-xl sm:text-2xl font-bold">Stil Atașament</h2><p className="text-slate-600 text-sm">Nivel 2 — Dimensiunea 1</p></div>
                    </div>
                    <div className="bg-purple-50 rounded-xl p-6 border border-purple-200 mb-4">
                        <div className="text-3xl font-bold text-purple-900 mb-3">{attachment.style}</div>
                        <p className="text-slate-700 mb-4">{attachment.info?.desc}</p>
                        {attachment.info?.strengths && (
                            <div className="grid md:grid-cols-2 gap-4 mt-4">
                                <div className="bg-white rounded-lg p-4">
                                    <div className="text-sm font-medium text-green-600 mb-1">✅ Puncte forte</div>
                                    <p className="text-sm text-slate-700">{attachment.info.strengths}</p>
                                </div>
                                <div className="bg-white rounded-lg p-4">
                                    <div className="text-sm font-medium text-orange-600 mb-1">⚡ Provocări</div>
                                    <p className="text-sm text-slate-700">{attachment.info.challenges}</p>
                                </div>
                            </div>
                        )}
                        {attachment.info?.compat && (
                            <div className="bg-white rounded-lg p-4 mt-4">
                                <div className="text-sm font-medium text-purple-600 mb-1">💕 Compatibilitate</div>
                                <p className="text-sm text-slate-700">{attachment.info.compat}</p>
                            </div>
                        )}
                        {attachment.info?.healing && (
                            <div className="bg-white rounded-lg p-4 mt-3">
                                <div className="text-sm font-medium text-blue-600 mb-1">🌱 Recomandare de creștere</div>
                                <p className="text-sm text-slate-700">{attachment.info.healing}</p>
                            </div>
                        )}
                    </div>
                    <AIAnalysisBox
                        dimension="nivel2_dim1"
                        dimensionData={{ style: attachment.style, scores: attachment.scores }}
                        borderColor="border-purple-200" bgColor="bg-purple-50" accentColor="text-purple-600"
                    />
                </div>
            )}

            {/* Values */}
            {values && topValues[0]?.[1] > 0 && (
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-4 sm:p-8 mb-6 sm:mb-8">
                    <div className="flex items-center mb-6 pb-4 border-b">
                        <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mr-4"><Compass className="text-cyan-600" size={24} /></div>
                        <div><h2 className="text-xl sm:text-2xl font-bold">Valori Relaționale</h2><p className="text-slate-600 text-sm">Nivel 2 — Dimensiunea 2</p></div>
                    </div>
                    <div className="space-y-4">
                        {topValues.map(([v, c], i) => c > 0 && (
                            <div key={v} className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                                <div className="flex justify-between mb-3">
                                    <div className="flex items-center">
                                        <div className="w-8 h-8 bg-cyan-600 rounded-full flex items-center justify-center text-white font-bold mr-3">{i + 1}</div>
                                        <div className="text-lg font-semibold">{valueLabels[v]}</div>
                                    </div>
                                    <div className="text-right"><div className="text-2xl font-bold text-cyan-600">{c}</div><div className="text-xs text-slate-600">din 5</div></div>
                                </div>
                                <div className="w-full bg-slate-200 rounded-full h-3"><div className="bg-cyan-600 h-3 rounded-full" style={{ width: `${(c / 5) * 100}%` }} /></div>
                            </div>
                        ))}
                    </div>
                    <AIAnalysisBox
                        dimension="nivel2_dim2"
                        dimensionData={{ values }}
                        borderColor="border-cyan-200" bgColor="bg-cyan-50" accentColor="text-cyan-600"
                    />
                </div>
            )}

            {/* Maturity — Enhanced */}
            {maturity && (
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-4 sm:p-8 mb-6 sm:mb-8">
                    <div className="flex items-center mb-6 pb-4 border-b">
                        <div className={`w-12 h-12 ${mColors.bg} rounded-full flex items-center justify-center mr-4`}><Brain className={mColors.text} size={24} /></div>
                        <div><h2 className="text-xl sm:text-2xl font-bold">Maturitate Emoțională</h2><p className="text-slate-600 text-sm">Nivel 2 — Dimensiunea 3</p></div>
                    </div>
                    <div className={`${mColors.bg} rounded-xl p-6 border ${mColors.border}`}>
                        <div className="flex justify-between mb-4">
                            <div>
                                <div className="text-sm font-medium text-slate-600 mb-1">SCOR</div>
                                <div className={`text-4xl font-bold ${mColors.text}`}>{maturity.totalScore}<span className="text-2xl">/{maturity.maxPossible}</span></div>
                            </div>
                            <div className="text-right">
                                <div className="text-sm font-medium text-slate-600 mb-1">PROFIL</div>
                                <div className={`text-xl font-semibold ${mColors.text}`}>{maturity.profile}</div>
                            </div>
                        </div>
                        <div className="w-full bg-white rounded-full h-4 mb-4"><div className={`${mColors.bar} h-4 rounded-full`} style={{ width: `${(maturity.totalScore / maturity.maxPossible) * 100}%` }} /></div>
                        <p className="text-slate-700 font-medium mb-2">{maturity.description}</p>
                        <p className="text-slate-600 text-sm">{maturity.detailedDesc}</p>
                    </div>
                    <AIAnalysisBox
                        dimension="nivel2_dim3"
                        dimensionData={{ totalScore: maturity.totalScore, profile: maturity.profile }}
                        borderColor={mColors.border} bgColor={mColors.bg} accentColor={mColors.text}
                    />
                </div>
            )}

            {/* Love Languages */}
            {loveLanguages && topLanguages[0]?.[1] > 0 && (
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-4 sm:p-8 mb-6 sm:mb-8">
                    <div className="flex items-center mb-6 pb-4 border-b">
                        <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mr-4"><Heart className="text-pink-600" size={24} /></div>
                        <div><h2 className="text-xl sm:text-2xl font-bold">Limbaje Iubirii</h2><p className="text-slate-600 text-sm">Nivel 2 — Dimensiunea 4</p></div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                        {topLanguages.map(([l, s], i) => s > 0 && (
                            <div key={l} className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                                <div className="text-sm font-medium text-pink-600 mb-2">#{i + 1}</div>
                                <div className="text-lg font-bold mb-2">{loveLanguageLabels[l]}</div>
                                <div className="flex items-end"><div className="text-3xl font-bold text-pink-600">{s}</div><div className="text-sm text-slate-600 ml-2">/5</div></div>
                            </div>
                        ))}
                    </div>
                    <AIAnalysisBox
                        dimension="nivel2_dim4"
                        dimensionData={{ languages: loveLanguages }}
                        borderColor="border-pink-200" bgColor="bg-pink-50" accentColor="text-pink-600"
                    />
                </div>
            )}

            {/* Emotional Intelligence — Enhanced */}
            {emotionalIQ && (
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-4 sm:p-8 mb-6 sm:mb-8">
                    <div className="flex items-center mb-6 pb-4 border-b">
                        <div className={`w-12 h-12 ${eqColors.bg} rounded-full flex items-center justify-center mr-4`}><Brain className={eqColors.text} size={24} /></div>
                        <div><h2 className="text-xl sm:text-2xl font-bold">Inteligență Emoțională</h2><p className="text-slate-600 text-sm">Nivel 2 — Dimensiunea 5</p></div>
                    </div>
                    <div className={`${eqColors.bg} rounded-xl p-6 border ${eqColors.border}`}>
                        <div className="flex justify-between mb-4">
                            <div>
                                <div className="text-sm font-medium text-slate-600 mb-1">SCOR</div>
                                <div className={`text-4xl font-bold ${eqColors.text}`}>{emotionalIQ.totalScore}<span className="text-2xl">/{emotionalIQ.maxPossible}</span></div>
                            </div>
                            <div className="text-right">
                                <div className="text-sm font-medium text-slate-600 mb-1">PROFIL</div>
                                <div className={`text-lg font-semibold ${eqColors.text}`}>{emotionalIQ.profile}</div>
                            </div>
                        </div>
                        <div className="w-full bg-white rounded-full h-4 mb-4"><div className={`${eqColors.bar} h-4 rounded-full`} style={{ width: `${(emotionalIQ.totalScore / emotionalIQ.maxPossible) * 100}%` }} /></div>
                        <p className="text-slate-700 font-medium mb-2">{emotionalIQ.description}</p>
                        <p className="text-slate-600 text-sm">{emotionalIQ.detailedDesc}</p>
                    </div>
                    <AIAnalysisBox
                        dimension="nivel2_dim5"
                        dimensionData={{ totalScore: emotionalIQ.totalScore, profile: emotionalIQ.profile }}
                        borderColor={eqColors.border} bgColor={eqColors.bg} accentColor={eqColors.text}
                    />
                </div>
            )}

            {/* ==================== NIVEL 3 ==================== */}

            {subconscious && (
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-4 sm:p-8 mb-6 sm:mb-8">
                    <div className="flex items-center mb-6 pb-4 border-b">
                        <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mr-4"><Brain className="text-indigo-600" size={24} /></div>
                        <div><h2 className="text-xl sm:text-2xl font-bold">Motivații Subconștiente</h2><p className="text-slate-600 text-sm">Nivel 3</p></div>
                    </div>
                    <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200 mb-4">
                        <div className="text-sm font-medium text-indigo-600 mb-2">TIPAR DOMINANT</div>
                        <div className="text-2xl font-bold text-indigo-900 mb-3">{subconscious.dominant.name}</div>
                        <p className="text-slate-700 mb-4">{subconscious.dominant.desc}</p>
                    </div>

                    {/* Detailed interpretation */}
                    <div className="space-y-4">
                        <div className="bg-red-50 rounded-xl p-5 border border-red-200">
                            <div className="text-sm font-medium text-red-600 mb-2">🩹 Rana de origine</div>
                            <p className="text-sm text-slate-700">{subconscious.dominant.wound}</p>
                        </div>
                        <div className="bg-amber-50 rounded-xl p-5 border border-amber-200">
                            <div className="text-sm font-medium text-amber-600 mb-2">🔄 Dinamica relațională</div>
                            <p className="text-sm text-slate-700">{subconscious.dominant.dynamics}</p>
                        </div>
                        <div className="bg-green-50 rounded-xl p-5 border border-green-200">
                            <div className="text-sm font-medium text-green-600 mb-2">🌱 Vindecare</div>
                            <p className="text-sm text-slate-700">{subconscious.dominant.healing}</p>
                        </div>
                        <div className="bg-purple-50 rounded-xl p-5 border border-purple-200">
                            <div className="text-sm font-medium text-purple-600 mb-2">💕 Notă de compatibilitate</div>
                            <p className="text-sm text-slate-700">{subconscious.dominant.compatNote}</p>
                        </div>
                    </div>

                    {/* Pattern distribution */}
                    <div className="mt-6">
                        <button onClick={() => toggleSection('n3dist')} className="flex items-center text-sm text-indigo-600 hover:text-indigo-800 font-medium">
                            {expandedSections.n3dist ? <ChevronUp size={16} className="mr-1" /> : <ChevronDown size={16} className="mr-1" />}
                            Distribuția tiparelor
                        </button>
                        {expandedSections.n3dist && (
                            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                                {Object.entries(subconscious.scores).map(([key, val]) => {
                                    const names = { a: 'Indisponibilitate', b: 'Salvator', c: 'Haos', d: 'Repetiție parentală' };
                                    return (
                                        <div key={key} className={`rounded-lg p-3 text-center border ${key === subconscious.dominantKey ? 'bg-indigo-100 border-indigo-300' : 'bg-slate-50 border-slate-200'}`}>
                                            <div className="text-xs text-slate-600 mb-1">{names[key]}</div>
                                            <div className={`text-xl font-bold ${key === subconscious.dominantKey ? 'text-indigo-700' : 'text-slate-700'}`}>{val}/7</div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <AIAnalysisBox
                        dimension="nivel3"
                        dimensionData={{ dominantName: subconscious.dominant.name, dominantDesc: subconscious.dominant.desc, scores: subconscious.scores }}
                        borderColor="border-indigo-200" bgColor="bg-indigo-50" accentColor="text-indigo-600"
                    />
                </div>
            )}

            {/* ==================== NIVEL 4 — MCMI ==================== */}

            {personality && (
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-4 sm:p-8 mb-6 sm:mb-8">
                    <div className="flex items-center mb-6 pb-4 border-b">
                        <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mr-4"><Users className="text-emerald-600" size={24} /></div>
                        <div><h2 className="text-xl sm:text-2xl font-bold">Profil de Personalitate</h2><p className="text-slate-600 text-sm">Nivel 4 — MCMI — 16 dimensiuni</p></div>
                    </div>

                    {/* Risk Flags */}
                    {personality.riskFlags.length > 0 && (
                        <div className="bg-red-50 rounded-xl p-6 border border-red-200 mb-6">
                            <div className="flex items-center mb-3">
                                <AlertTriangle className="text-red-600 mr-2" size={20} />
                                <div className="text-lg font-bold text-red-900">Semnale de Atenție</div>
                            </div>
                            <p className="text-sm text-red-700 mb-4">Următoarele trăsături au scoruri ridicate și pot indica zone care necesită atenție sau suport profesional:</p>
                            <div className="space-y-2">
                                {personality.riskFlags.map(([cat, data]) => (
                                    <div key={cat} className="flex items-center justify-between bg-white rounded-lg p-3 border border-red-100">
                                        <div className="flex items-center">
                                            <span className="mr-2">{mcmiLabels[cat]?.icon}</span>
                                            <span className="font-semibold text-red-900">{mcmiLabels[cat]?.friendly || cat}</span>
                                        </div>
                                        <span className="text-red-600 font-bold">{data.average.toFixed(1)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Vulnerabilities */}
                    {personality.vulnerabilities.length > 0 && personality.vulnerabilities.length !== personality.riskFlags.length && (
                        <div className="bg-amber-50 rounded-xl p-6 border border-amber-200 mb-6">
                            <div className="flex items-center mb-3">
                                <Shield className="text-amber-600 mr-2" size={20} />
                                <div className="text-lg font-bold text-amber-900">Vulnerabilități (≥3.6)</div>
                            </div>
                            <div className="space-y-2">
                                {personality.vulnerabilities.map(([cat, data]) => (
                                    <div key={cat} className="flex items-center justify-between bg-white rounded-lg p-3 border border-amber-100">
                                        <div className="flex items-center">
                                            <span className="mr-2">{mcmiLabels[cat]?.icon}</span>
                                            <span className="font-semibold text-amber-900">{mcmiLabels[cat]?.friendly || cat}</span>
                                        </div>
                                        <span className="text-amber-600 font-bold">{data.average.toFixed(1)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Top 3 */}
                    <div className="mb-6">
                        <div className="text-lg font-bold text-slate-900 mb-4">Top 3 Trăsături Dominante</div>
                        <div className="space-y-4">
                            {personality.topTraits.map(([cat, data], i) => {
                                const label = mcmiLabels[cat] || { friendly: cat, desc: '', icon: '📊' };
                                const levelColors = {
                                    green: { bg: 'bg-green-100', text: 'text-green-700', badge: 'bg-green-200' },
                                    yellow: { bg: 'bg-yellow-100', text: 'text-yellow-700', badge: 'bg-yellow-200' },
                                    red: { bg: 'bg-red-100', text: 'text-red-700', badge: 'bg-red-200' }
                                };
                                const lc = levelColors[data.levelColor] || levelColors.green;
                                return (
                                    <div key={cat} className="bg-emerald-50 rounded-xl p-4 sm:p-6 border border-emerald-200">
                                        <div className="flex items-start gap-3 sm:gap-4">
                                            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 text-sm sm:text-base">{i + 1}</div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between gap-2">
                                                    <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                                                        <span className="text-lg sm:text-xl">{label.icon}</span>
                                                        <span className="text-base sm:text-lg font-bold text-slate-900 break-words">{label.friendly}</span>
                                                        <span className={`${lc.badge} ${lc.text} text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap`}>{data.level}</span>
                                                    </div>
                                                    <div className="text-right flex-shrink-0">
                                                        <div className="text-xl sm:text-2xl font-bold text-emerald-600">{data.average.toFixed(1)}</div>
                                                        <div className="text-xs text-slate-500">din 5</div>
                                                    </div>
                                                </div>
                                                <p className="text-xs sm:text-sm text-slate-600 mt-1">{label.desc}</p>
                                            </div>
                                        </div>
                                        <div className="mt-3 w-full bg-emerald-200 rounded-full h-2">
                                            <div className="bg-emerald-600 h-2 rounded-full" style={{ width: `${(data.average / 5) * 100}%` }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* All 16 Categories — Expandable */}
                    <div>
                        <button
                            onClick={() => toggleSection('mcmiAll')}
                            className="flex items-center text-emerald-600 hover:text-emerald-800 font-semibold text-sm mb-4"
                        >
                            {expandedSections.mcmiAll ? <ChevronUp size={16} className="mr-1" /> : <ChevronDown size={16} className="mr-1" />}
                            {expandedSections.mcmiAll ? 'Ascunde toate dimensiunile' : 'Vezi toate cele 16 dimensiuni'}
                        </button>

                        {expandedSections.mcmiAll && (
                            <div className="space-y-3">
                                {personality.allSorted.map(([cat, data]) => {
                                    const label = mcmiLabels[cat] || { friendly: cat, desc: '', icon: '📊' };
                                    const isRisk = riskSet.includes(cat) && data.average > 3.5;
                                    const barColor = data.levelColor === 'red' ? 'bg-red-500' : data.levelColor === 'yellow' ? 'bg-yellow-500' : 'bg-green-500';
                                    const bgColor = isRisk ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200';

                                    return (
                                        <div key={cat} className={`rounded-xl p-3 sm:p-4 border ${bgColor}`}>
                                            <div className="flex items-center justify-between gap-2 mb-2">
                                                <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-wrap">
                                                    <span className="text-sm sm:text-base">{label.icon}</span>
                                                    <span className="font-semibold text-sm sm:text-base text-slate-900 break-words">{label.friendly}</span>
                                                    {isRisk && <AlertTriangle className="text-red-500 flex-shrink-0" size={14} />}
                                                </div>
                                                <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                                                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${data.levelColor === 'red' ? 'bg-red-100 text-red-700' : data.levelColor === 'yellow' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                                                        {data.level}
                                                    </span>
                                                    <span className="font-bold text-slate-700 text-sm sm:text-base">{data.average.toFixed(1)}</span>
                                                </div>
                                            </div>
                                            <div className="w-full bg-slate-200 rounded-full h-2">
                                                <div className={`${barColor} h-2 rounded-full`} style={{ width: `${(data.average / 5) * 100}%` }} />
                                            </div>
                                            <p className="text-xs text-slate-500 mt-1">{label.desc}</p>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <AIAnalysisBox
                        dimension="nivel4_mcmi"
                        dimensionData={{
                            allScores: personality.allSorted,
                            topTraits: personality.topTraits,
                            vulnerabilities: personality.vulnerabilities,
                            riskFlags: personality.riskFlags
                        }}
                        borderColor="border-emerald-200" bgColor="bg-emerald-50" accentColor="text-emerald-600"
                    />
                </div>
            )}

            {/* ==================== RESET ==================== */}
            <div className="bg-white rounded-2xl shadow-lg border border-red-200 p-8 mb-8">
                <div className="flex items-center mb-6 pb-4 border-b border-red-200">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4"><Trash2 className="text-red-600" size={24} /></div>
                    <div><h2 className="text-xl sm:text-2xl font-bold">Resetează Testele</h2><p className="text-slate-600 text-sm">Șterge toate răspunsurile</p></div>
                </div>
                <div className="bg-red-50 rounded-xl p-6 border border-red-200 mb-6">
                    <div className="flex items-start gap-3">
                        <div className="text-red-600 text-2xl">⚠️</div>
                        <div><h3 className="font-bold text-red-900 mb-2">Atenție!</h3>
                            <p className="text-sm text-red-800">Răspunsurile vor fi șterse permanent. Acțiune ireversibilă. Vei reface testele de la început. Abonamentul rămâne activ.</p>
                        </div>
                    </div>
                </div>
                <div className="flex items-center justify-between">
                    <div><div className="text-3xl font-bold text-red-600">39 €</div><div className="text-sm text-slate-600">Plată unică</div></div>
                    <button onClick={handleResetTests} disabled={isResetting} className={`flex items-center gap-2 px-8 py-4 rounded-xl font-semibold transition shadow-lg ${isResetting ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700 text-white'}`}>
                        {isResetting ? <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>Se procesează...</> : <><CreditCard size={20} />Resetează</>}
                    </button>
                </div>
            </div>

            {/* Continue */}
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-4 sm:p-8 text-center">
                <h3 className="text-xl font-bold mb-4">Continuă testele</h3>
                <p className="text-slate-600 mb-6">Pentru rezultate complete pe toate cele 4 niveluri</p>
                <button onClick={() => window.location.href = (!subscriptionLoaded || hasSubscription) ? '/teste' : '/pricing'} className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-semibold transition shadow-md inline-flex items-center gap-2">
                    Continuă <ArrowRight size={20} />
                </button>
            </div>
        </div>
    );
};

export default ProfileResults;